/** 同类目下开启自动规则的模板：优先级数字越小越优先。 */

/** @param {unknown} value */
export function normalizeTemplateStyleNo(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * 按款号精确筛选（忽略大小写；空款号返回空数组）。
 * @param {Array} items
 * @param {string} styleNo
 */
export function filterTemplatesByStyleNo(items = [], styleNo = '') {
  const key = normalizeTemplateStyleNo(styleNo);
  if (!key) return [];
  return (Array.isArray(items) ? items : []).filter(
    (item) => normalizeTemplateStyleNo(item?.styleNo) === key,
  );
}

function isAutoRuleEnabled(item) {
  return Boolean(
    item
    && (item.autoRuleEnabled === true || item.autoRuleEnabled === 1)
    && item.enabled !== false
    && item.enabled !== 0,
  );
}

/**
 * 挑选自动规则模板。
 * @param {Array} items 通常已按类目（productTypeId）收窄
 * @param {{ styleNo?: string }} [options]
 * - 有 styleNo：优先同款号且开启自动规则；无命中再回退「未填款号」的类目自动规则
 * - 无 styleNo：同类目开启自动规则中优先级最高（原逻辑）
 */
export function pickAutoRuleTemplate(items = [], options = null) {
  const list = (Array.isArray(items) ? items : []).filter(isAutoRuleEnabled);
  const styleNo = String(options?.styleNo || '').trim();
  if (styleNo) {
    const byStyle = filterTemplatesByStyleNo(list, styleNo);
    if (byStyle.length) {
      return sortTemplatesByPriority(byStyle)[0] || null;
    }
    const generic = list.filter((item) => !String(item?.styleNo || '').trim());
    if (generic.length) {
      return sortTemplatesByPriority(generic)[0] || null;
    }
    return null;
  }
  return sortTemplatesByPriority(list)[0] || null;
}

/** 按优先级排序（数字越小越优先；同优先级按 id 升序） */
export function sortTemplatesByPriority(items = []) {
  return (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const ao = Number(a?.sortOrder);
      const bo = Number(b?.sortOrder);
      const aOrder = Number.isFinite(ao) ? ao : 0;
      const bOrder = Number.isFinite(bo) ? bo : 0;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
}

/** 取优先级最高的模板（不限制自动规则开关） */
export function pickHighestPriorityTemplate(items = []) {
  return sortTemplatesByPriority(items)[0] || null;
}

/**
 * 按商品类目筛出可参与自动规则的模板。
 * 优先 productTypeId，其次 categoryId，再次 categoryPathLabel。
 * 任一层对不上则继续下一层；三层都对不上返回空，绝不回退成「全部模板」。
 * 若商品带 spuName：优先匹配绑定了该 SPU 的模板，否则回退到未绑定 SPU 的类目模板。
 */
export function filterTemplatesByProductCategory(items = [], productMeta = null) {
  const list = Array.isArray(items) ? items : [];
  const productTypeId = String(productMeta?.productTypeId || '').trim();
  const categoryId = String(productMeta?.categoryId || '').trim();
  const categoryPathLabel = String(productMeta?.categoryPathLabel || '').trim();
  const spuName = String(productMeta?.spuName || productMeta?.platformSpuId || '').trim();

  if (!productTypeId && !categoryId && !categoryPathLabel) {
    return [];
  }

  let scoped = [];
  if (productTypeId) {
    scoped = list.filter((item) => String(item?.productTypeId || '') === productTypeId);
  }
  if (!scoped.length && categoryId) {
    scoped = list.filter((item) => String(item?.categoryId || '') === categoryId);
  }
  if (!scoped.length && categoryPathLabel) {
    scoped = list.filter(
      (item) => String(item?.categoryPathLabel || '') === categoryPathLabel,
    );
  }
  if (!scoped.length) {
    return [];
  }

  if (!spuName) {
    // 商品无 SPU：只用未绑定 SPU 的类目模板，避免误套 SPU 专属模板
    return scoped.filter((item) => !String(item?.spuName || '').trim());
  }
  const bySpu = scoped.filter((item) => String(item?.spuName || '').trim() === spuName);
  if (bySpu.length) return bySpu;
  return scoped.filter((item) => !String(item?.spuName || '').trim());
}
